import React from "react";
import { Save, RefreshCw, CheckCircle, AlertCircle, Loader2 } from "lucide-react";
import { ScoringRulesConfig } from "../../../components/judging/ScoringRulesConfig";
import { useRulesSettings } from "../../../hooks/useRulesSettings";

/**
 * Rules Settings Component
 * Dedicated tab for managing scoring rules separate from awards
 */
export function RulesSettings() {
  const {
    scoringRules,
    loading,
    error,
    hasChanges,
    successMessage,
    updateScoringRules,
    saveRules,
    resetRules,
    validateRules
  } = useRulesSettings();

  const handleSave = async () => {
    // Validate rules before saving
    const validation = validateRules();
    if (!validation.valid) {
      console.error('Validation errors:', validation.errors);
      return;
    }

    const success = await saveRules();
    if (success) {
      console.log('Scoring rules saved successfully');
    }
  };

  const handleReset = () => {
    if (hasChanges && window.confirm('Are you sure you want to discard your changes?')) {
      resetRules();
    }
  };

  return (
    <div className="space-y-6">

      {/* Success Message */}
      {successMessage && (
        <div className="alert alert-success">
          <CheckCircle className="w-5 h-5" />
          <span>{successMessage}</span>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="alert alert-error">
          <AlertCircle className="w-5 h-5" />
          <span>{error}</span>
        </div>
      )}

      {/* Unsaved Changes Warning */}
      {hasChanges && (
        <div className="alert alert-warning">
          <AlertCircle className="w-5 h-5" />
          <span>You have unsaved changes. Don't forget to save your scoring rules!</span>
        </div>
      )}

      {/* Scoring Rules Configuration */}
      <div className="card bg-base-100 border border-base-300">
        <div className="card-body">
          {/* Header with Save Button */}
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-6">
            <div>
              <h2 className="text-2xl font-bold text-base-content">
                Scoring Rules Configuration
              </h2>
              <p className="text-base-content/60 mt-1">
                Configure flexible judge scoring rules for your competition
              </p>
            </div>
            
            {/* Action Buttons */}
            <div className="flex gap-3">
              {hasChanges && (
                <button
                  onClick={handleReset}
                  className="btn btn-ghost btn-sm"
                  disabled={loading}
                >
                  <RefreshCw className="w-4 h-4" />
                  Reset
                </button>
              )}
              
              <button
                onClick={handleSave}
                className={`btn btn-sm ${hasChanges ? 'btn-primary' : 'btn-ghost'}`}
                disabled={loading || !hasChanges}
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                {loading ? 'Saving...' : 'Save Rules'}
              </button>
            </div>
          </div>

          {loading && !hasChanges ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
              <span className="ml-3 text-base-content/60">Loading scoring rules...</span>
            </div>
          ) : (
            <ScoringRulesConfig
              scoringRules={scoringRules}
              onChange={updateScoringRules}
            />
          )}
        </div>
      </div>
    </div>
  );
}
